import React, { useEffect, useState, useCallback } from 'react';
import api, { fmtINR, fmtNum, fmtDate } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Users, Search, Plus, Download, IndianRupee, Loader2, Printer, FileDown, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { printPartyStatement } from '@/lib/pumpPrint';

const PAY_METHODS = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Card', 'Other'];

export default function PumpCRM() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ from: '', to: '' });
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [newParty, setNewParty] = useState('');
  const [pay, setPay] = useState({ amount: '', method: 'Cash', date: new Date().toISOString().slice(0, 10), note: '' });
  const [paying, setPaying] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (range.from) params.from = range.from;
    if (range.to) params.to = range.to;
    api.get('/pump/crm', { params }).then((r) => setData(r.data)).catch(() => toast.error('Could not load CRM data'));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const openLedger = async (party) => {
    setSel(party);
    setLedger(null);
    try {
      const params = { party };
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      const r = await api.get('/pump/crm/ledger', { params });
      setLedger(r.data);
    } catch (e) {
      toast.error('Could not load ledger');
    }
  };

  const addParty = async () => {
    if (newParty.trim().length < 2) return;
    try {
      await api.post('/pump/masters/parties', { name: newParty.trim() });
      toast.success('Party added to master');
      setNewParty('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // Record money received from the selected party; outstanding reduces automatically.
  const recordPayment = async () => {
    const amount = Number(pay.amount);
    if (!amount || amount <= 0) return toast.error('Enter a valid amount');
    setPaying(true);
    try {
      await api.post('/pump/crm/payments', { party: sel, amount, method: pay.method, date: pay.date, note: pay.note });
      toast.success(`Payment of ₹${amount.toLocaleString('en-IN')} recorded for ${sel}`);
      setPay({ amount: '', method: 'Cash', date: new Date().toISOString().slice(0, 10), note: '' });
      await openLedger(sel);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record payment'); }
    finally { setPaying(false); }
  };

  // Download the party statement as PDF or Excel (respects the CRM date range).
  const [dlBusy, setDlBusy] = useState(false);
  const downloadStatement = async (kind) => {
    if (!sel) return;
    setDlBusy(true);
    try {
      const params = { party: sel };
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      const r = await api.get(`/pump/crm/ledger/${kind}`, { params, responseType: 'blob' });
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `party-statement-${sel.replace(/[^a-zA-Z0-9]+/g, '-')}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Statement ${kind === 'pdf' ? 'PDF' : 'Excel'} downloaded`);
    } catch (e) { toast.error('Could not download the statement'); }
    finally { setDlBusy(false); }
  };

  const rows = (data?.parties || []).filter((p) => p.party.toLowerCase().includes(q.toLowerCase()));
  const grand = rows.reduce((acc, p) => ({ txns: acc.txns + p.txns, qty: acc.qty + p.total_qty, amount: acc.amount + p.total_amount, paid: acc.paid + (p.paid || 0), outstanding: acc.outstanding + (p.outstanding ?? p.total_amount) }), { txns: 0, qty: 0, amount: 0, paid: 0, outstanding: 0 });

  const exportCSV = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      ['Party', 'Transactions', 'Qty (L)', 'Credit Amount (Rs)', 'Paid (Rs)', 'Outstanding (Rs)', 'Channels', 'Last Purchase'].join(','),
      ...rows.map((p) => [esc(p.party), p.txns, p.total_qty, p.total_amount, p.paid || 0, p.outstanding ?? p.total_amount, esc(p.channels.join(' | ')), p.last_date].join(',')),
      ['TOTAL', grand.txns, Math.round(grand.qty * 100) / 100, Math.round(grand.amount * 100) / 100, Math.round(grand.paid * 100) / 100, Math.round(grand.outstanding * 100) / 100, '', ''].join(','),
    ];
    const label = [range.from || 'start', range.to || 'today'].join('_to_');
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `party-credit-${label}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${rows.length} parties`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Party CRM — Credit Ledger</h2>
          <p className="text-sm text-muted-foreground">{data ? `${data.count} parties with credit activity` : 'Loading…'}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="text-xs text-muted-foreground">From</label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-9" data-testid="crm-filter-from" /></div>
          <div><label className="text-xs text-muted-foreground">To</label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-9" data-testid="crm-filter-to" /></div>
          <Button variant="secondary" className="h-9" onClick={exportCSV} disabled={!data || rows.length === 0} data-testid="crm-export-button"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
          <div className="flex items-end gap-1">
            <Input value={newParty} onChange={(e) => setNewParty(e.target.value)} placeholder="New party name" className="h-9 w-48" data-testid="crm-new-party-input" />
            <Button variant="secondary" className="h-9" onClick={addParty} data-testid="crm-add-party-button"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search party…" className="pl-9" data-testid="crm-search-input" />
      </div>

      {!data ? <Skeleton className="h-96 w-full" /> : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs text-muted-foreground">
                <tr>{['Party', 'Txns', 'Qty (L)', 'Credit Amount', 'Paid', 'Outstanding', 'Channels', 'Last Purchase', ''].map((h) => <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.party} className="border-t border-border hover:bg-muted/50 cursor-pointer" onClick={() => openLedger(p.party)} data-testid={`crm-row-${p.party.slice(0, 12).replace(/[^a-zA-Z0-9]/g, '-')}`}>
                    <td className="px-3 py-2 font-medium max-w-[300px] truncate">{p.party}</td>
                    <td className="px-3 py-2">{p.txns}</td>
                    <td className="px-3 py-2">{fmtNum(p.total_qty)}</td>
                    <td className="px-3 py-2">{fmtINR(p.total_amount)}</td>
                    <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400">{p.paid ? fmtINR(p.paid) : '-'}</td>
                    <td className={`px-3 py-2 font-semibold ${(p.outstanding ?? p.total_amount) > 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>{fmtINR(p.outstanding ?? p.total_amount)}</td>
                    <td className="px-3 py-2"><div className="flex gap-1 flex-wrap">{p.channels.map((c) => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}</div></td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(p.last_date)}</td>
                    <td className="px-3 py-2 text-primary text-xs font-medium">Ledger</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No parties found.</td></tr>}
                {rows.length > 0 && (
                  <tr className="border-t-2 border-border bg-amber-100 dark:bg-amber-950/60 font-bold sticky bottom-0" data-testid="crm-grand-total-row">
                    <td className="px-3 py-2">TOTAL ({rows.length} parties)</td>
                    <td className="px-3 py-2">{grand.txns}</td>
                    <td className="px-3 py-2">{fmtNum(grand.qty)}</td>
                    <td className="px-3 py-2" data-testid="crm-grand-total-amount">{fmtINR(grand.amount)}</td>
                    <td className="px-3 py-2">{fmtINR(grand.paid)}</td>
                    <td className="px-3 py-2" data-testid="crm-grand-outstanding">{fmtINR(grand.outstanding)}</td>
                    <td colSpan={3} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="crm-ledger-sheet">
          <SheetHeader><SheetTitle className="text-base">{sel}</SheetTitle></SheetHeader>
          {!ledger ? <Skeleton className="h-64 w-full mt-4" /> : (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <Button variant="secondary" onClick={() => printPartyStatement(ledger, range)} data-testid="party-statement-print-button">
                  <Printer className="h-4 w-4 mr-1.5" />Print A4
                </Button>
                <Button variant="secondary" onClick={() => downloadStatement('pdf')} disabled={dlBusy} data-testid="party-statement-pdf-button">
                  {dlBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}PDF
                </Button>
                <Button variant="secondary" onClick={() => downloadStatement('excel')} disabled={dlBusy} data-testid="party-statement-excel-button">
                  {dlBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}Excel
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg border border-border p-2.5"><p className="text-xs text-muted-foreground">Credit Total</p><p className="font-semibold" data-testid="ledger-total">{fmtINR(ledger.total_amount)}</p></div>
                <div className="rounded-lg border border-border p-2.5"><p className="text-xs text-muted-foreground">Paid</p><p className="font-semibold text-emerald-600 dark:text-emerald-400" data-testid="ledger-paid">{fmtINR(ledger.total_paid || 0)}</p></div>
                <div className="rounded-lg border border-border p-2.5 col-span-2"><p className="text-xs text-muted-foreground">Outstanding</p><p className={`text-lg font-bold ${(ledger.outstanding ?? ledger.total_amount) > 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`} data-testid="ledger-outstanding">{fmtINR(ledger.outstanding ?? ledger.total_amount)}</p></div>
              </div>

              {/* Record a payment from this party */}
              <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2" data-testid="party-payment-form">
                <p className="text-sm font-semibold flex items-center gap-1.5"><IndianRupee className="h-4 w-4 text-primary" />Record Payment Received</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" min="1" placeholder="Amount (₹)" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="party-payment-amount" />
                  <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                    <SelectTrigger data-testid="party-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="party-payment-date" />
                  <Input placeholder="Note (optional)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} data-testid="party-payment-note" />
                </div>
                <Button className="w-full" onClick={recordPayment} disabled={paying} data-testid="party-payment-save">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Payment'}
                </Button>
              </div>

              {(ledger.payments || []).length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-1">Payments Received</p>
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground"><tr>{['Date', 'Method', 'Note', 'Amount'].map((h) => <th key={h} className="text-left px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
                    <tbody>
                      {ledger.payments.map((p) => (
                        <tr key={p.id} className="border-t border-border" data-testid={`payment-row-${p.id}`}>
                          <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(p.date)}</td>
                          <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{p.method}</Badge></td>
                          <td className="px-2 py-1.5 max-w-[140px] truncate">{p.note || '-'}</td>
                          <td className="px-2 py-1.5 font-medium text-emerald-600 dark:text-emerald-400">{fmtINR(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <p className="text-sm font-semibold mb-1">Credit Entries ({ledger.entries.length} · {fmtNum(ledger.total_qty)} L)</p>
              <table className="w-full text-xs">
                <thead className="text-muted-foreground"><tr>{['Date', 'Channel', 'Ref', 'Qty', 'Amount'].map((h) => <th key={h} className="text-left px-2 py-1.5 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {ledger.entries.map((e, i) => (
                    <tr key={`${e.date}-${i}`} className="border-t border-border">
                      <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(e.date)}</td>
                      <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{e.channel}</Badge></td>
                      <td className="px-2 py-1.5">{e.ref || '-'}</td>
                      <td className="px-2 py-1.5">{e.qty ? fmtNum(e.qty) : '-'}</td>
                      <td className="px-2 py-1.5 font-medium">{fmtINR(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
